'use client'

import { useEffect } from 'react'
import { create } from 'zustand'

const CONSENT_SERVICES = [
  'Usercentrics Consent Management Platform',
  'Google Tag Manager',
  'Google Analytics 4',
  'Instagram Content',
  'Spotify',
  'Hotjar',
  'GrowthBook',
  'YouTube Video',
  'Vimeo',
] as const satisfies readonly UsercentricsService[]

export const useConsentStore = create<ConsentState>(() => ({
  details: null,
  allAccepted: false,
  isLoading: true,
  openConsentPopup: (service: UsercentricsService) => {
    if (window.UC_UI) {
      const usercentricsUi = window.UC_UI

      const services = usercentricsUi.getServicesBaseInfo()
      const serviceData = services.find(({ name }) => name === service)
      if (serviceData) {
        usercentricsUi.showSecondLayer(serviceData.id)
      }
    }
  },
}))

const setDetails = (details: UsercentricsConsentState) => {
  useConsentStore.setState({ details, isLoading: false })
}

const finishLoading = () => {
  useConsentStore.setState({ isLoading: false })
}

const setAllAccepted = (allAccepted: boolean) => {
  useConsentStore.setState({ allAccepted })
}

const getConsentStateFromDetail = (
  detail: Pick<UsercentricsConsentDetail, UsercentricsService>
): UsercentricsConsentState =>
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Object.fromEntries loses key types
  Object.fromEntries(
    CONSENT_SERVICES.map(service => [service, detail[service]])
  ) as UsercentricsConsentState

const getConsentStateFromServicesBaseInfo = (
  services: ReturnType<UsercentricsUi['getServicesBaseInfo']>
): UsercentricsConsentState | null => {
  if (services.length === 0) {
    return null
  }

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Object.fromEntries loses key types
  const details = Object.fromEntries(
    CONSENT_SERVICES.map(service => [service, false])
  ) as UsercentricsConsentState

  for (const service of services) {
    const consentService = CONSENT_SERVICES.find(name => name === service.name)
    if (consentService) {
      details[consentService] = service.consent?.status
    }
  }

  return details
}

const updateConsentFromUsercentrics = (detail: UsercentricsConsentDetail) => {
  setDetails(getConsentStateFromDetail(detail))

  switch (detail.action) {
    case 'onInitialPageLoad':
      break
    case 'onAcceptAllServices':
      setAllAccepted(true)
      break
    case 'onDenyAllServices':
      setAllAccepted(false)
      break
    case 'onUpdateServices':
      break
    default:
      break
  }
}

const syncConsentStateFromUsercentricsUi = () => {
  if (typeof window === 'undefined' || !window.UC_UI) {
    return false
  }

  const usercentricsUi = window.UC_UI
  const details = getConsentStateFromServicesBaseInfo(usercentricsUi.getServicesBaseInfo())

  if (!details) {
    return false
  }

  setDetails(details)
  return true
}

export const useConsentSettings = () => {
  useEffect(() => {
    let syncIntervalId: number | null = null

    const stopPolling = () => {
      if (syncIntervalId !== null) {
        window.clearInterval(syncIntervalId)
        syncIntervalId = null
      }
    }

    const handleConsentEvent = (e: UsercentricsConsentEvent) => {
      if (e.detail?.event === 'consent_status') {
        updateConsentFromUsercentrics(e.detail)
        stopPolling()
      }
    }

    const handleScriptLoad = () => {
      if (syncConsentStateFromUsercentricsUi()) {
        stopPolling()
      }
    }

    window.addEventListener('ucEvent', handleConsentEvent)

    const usercentricsScript = document.getElementById('usercentrics-cmp')
    usercentricsScript?.addEventListener('load', handleScriptLoad)

    if (syncConsentStateFromUsercentricsUi()) {
      return () => {
        usercentricsScript?.removeEventListener('load', handleScriptLoad)
        window.removeEventListener('ucEvent', handleConsentEvent)
      }
    }

    if (!usercentricsScript) {
      finishLoading()
      return () => {
        window.removeEventListener('ucEvent', handleConsentEvent)
      }
    }

    let retriesLeft = 20

    syncIntervalId = window.setInterval(() => {
      retriesLeft -= 1

      if (syncConsentStateFromUsercentricsUi()) {
        stopPolling()
        return
      }

      if (retriesLeft <= 0) {
        finishLoading()
        stopPolling()
      }
    }, 250)

    return () => {
      stopPolling()
      usercentricsScript?.removeEventListener('load', handleScriptLoad)
      window.removeEventListener('ucEvent', handleConsentEvent)
    }
  }, [])
}

type UsercentricsConsentAction =
  | 'onInitialPageLoad'
  | 'onAcceptAllServices'
  | 'onDenyAllServices'
  | 'onUpdateServices'

export type UsercentricsService =
  | 'Usercentrics Consent Management Platform'
  | 'Google Tag Manager'
  | 'Google Analytics 4'
  | 'Instagram Content'
  | 'Spotify'
  | 'Hotjar'
  | 'GrowthBook'
  | 'YouTube Video'
  | 'Vimeo'

type UsercentricsConsentState = Record<UsercentricsService, boolean>

type UsercentricsConsentDetail = {
  'action': UsercentricsConsentAction
  'event': 'consent_status'
  'type': 'explicit' | 'implicit'
  'ucCategory': {
    essential: boolean
    marketing: boolean
    functional: boolean
  }
  'Usercentrics Consent Management Platform': boolean
  'Google Tag Manager': boolean
  'Google Analytics 4': boolean
  'Instagram Content': boolean
  'Spotify': boolean
  'Hotjar': boolean
  'GrowthBook': boolean
  'YouTube Video': boolean
  'Vimeo': boolean
}

type UsercentricsConsentEvent = CustomEvent<UsercentricsConsentDetail>

declare global {
  interface WindowEventMap {
    ucEvent: UsercentricsConsentEvent
  }
  interface Window {
    UC_UI?: UsercentricsUi
  }
}

type ConsentState = {
  details: UsercentricsConsentState | null
  isLoading: boolean
  allAccepted: boolean
  openConsentPopup: (service: UsercentricsService) => void
}

type UsercentricsUi = {
  showSecondLayer: (serviceId?: string) => void
  getServicesBaseInfo: () => Array<{
    id: string
    name: string
    categorySlug: string
    consent: { status: boolean }
  }>
}
